"use client";

import { Button } from "@papier/ui/components/ui/button";
import { Card, CardContent } from "@papier/ui/components/ui/card";
import { Plus, Trash2 } from "lucide-react";
import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";
import { getDefaultExampleForType } from "@/utils/schema-utils";
import { PrimitiveExampleEditor } from "./primitive-example-editor";
import { ObjectExampleEditor } from "./object-example-editor";
import { Label } from "@papier/ui/components/ui/label";

interface ArrayExampleEditorProps {
  example: any[];
  arrayField: SchemaField;
  onUpdate: (value: any[]) => void;
  onRemove: () => void;
}

export function ArrayExampleEditor({
  example,
  arrayField,
  onUpdate,
  onRemove,
}: ArrayExampleEditorProps) {
  const addItem = () => {
    const newItem = getDefaultExampleForType(
      arrayField.arrayItemType!.type,
      arrayField.arrayItemType!,
    );
    onUpdate([...example, newItem]);
  };

  const removeItem = (index: number) => {
    onUpdate(example.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, value: any) => {
    const newArray = [...example];
    newArray[index] = value;
    onUpdate(newArray);
  };

  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Array Example</Label>
          <Button variant="outline" size="sm" onClick={onRemove}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        {example.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-muted-foreground w-8 text-xs">[{index}]</span>
            {arrayField.arrayItemType!.type === "object" ? (
              <ObjectExampleEditor
                example={item}
                objectField={arrayField.arrayItemType!}
                onUpdate={(newItem) => updateItem(index, newItem)}
                onRemove={() => removeItem(index)}
                compact
              />
            ) : (
              <PrimitiveExampleEditor
                example={item}
                field={arrayField.arrayItemType!}
                onUpdate={(newItem) => updateItem(index, newItem)}
                onRemove={() => removeItem(index)}
              />
            )}
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addItem} className="w-full bg-transparent">
          <Plus className="mr-2 h-4 w-4" />
          Add Item
        </Button>
      </CardContent>
    </Card>
  );
}
